/// <reference path="../../typings/browser.d.ts" />

namespace Application.Services {
    'use strict';

    import AclEntry = Models.AclEntry;
    import IAclEntries = Models.IAclEntries;

    /**
     * Login service class
     */
    export class AclService {
        public static $name: string = "aclService";
        public static $inject: string[] = ['$q', 'backendService', '$interval', '$http'];
        public static AUTH_HEADER_COOKIE_KEY: string = 'flex_auth_header';

        private backendBaseUrl: string = window.backendUrl;
        private servicePath: string = '';
        private aclBackendUrl: string = this.backendBaseUrl + this.servicePath + 'acls';
        private aclEntriesBackendUrl: string = this.aclBackendUrl + '/aclEntries';
        private aclEntryBackendUrl: string = this.aclBackendUrl + '/aclEntry';

        private canAccessGuiQueue: IdMap = {};

        constructor(private $q: ng.IQService,
                    private backendService: BackendService,
                    private $interval: ng.IIntervalService,
                    private $http: ng.IHttpService) {
            this.initCanAccessGuiInterval();
        }

        public getAclsFor(entity: Models.IEntity, entityTypeName: string): ng.IPromise<IAclEntries> {
            let identityId: number = Common.Utils.idFromHref(entity.href);
            return this.backendService.get(this.aclBackendUrl + "/" + entityTypeName + "/" + identityId)
            .then((acls: ng.IHttpPromiseCallbackArg<IAclEntries>) => {
                let sorted: AclEntry[] = Models.AclEntry.sortAclEntries(acls.data.aclEntries);
                return {
                    identityClass: acls.data.identityClass,
                    identityId: acls.data.identityId,
                    aclEntries: sorted,
                } as IAclEntries;
            });
        }

        public getAclsForWebApplication(id: number, groupId: number): ng.IPromise<AclEntry> {
             return this.backendService.get(this.aclBackendUrl + "/aclEntryForSid/webApplications/" + id + "?groupId=" + groupId)
            .then((acl: ng.IHttpPromiseCallbackArg<AclEntry>) => {
                return acl.data;
            });
        }

        public getUserAclsForWebApplication(id: number, userId: number): ng.IPromise<AclEntry> {
            return this.backendService.get(this.aclBackendUrl + "/aclEntryForSid/webApplications/" + id + "?userId=" + userId)
                .then((acl: ng.IHttpPromiseCallbackArg<AclEntry>) => {
                    return acl.data;
                });
        }

        public getOrganizationUsersAclsForWebApplication(id: number, organizationId: number): ng.IPromise<AclEntry> {
            return this.backendService.get(`${this.aclBackendUrl}/aclEntryForSid/webApplications/${id}?organizationId=${organizationId}`)
                .then((acl: ng.IHttpPromiseCallbackArg<AclEntry>) => {
                    return acl.data;
                });
        }

        public getGrantedAuthorityAclsForWebApplication(id: number, grantedAuth: string): ng.IPromise<AclEntry> {
            return this.backendService.get(`${this.aclBackendUrl}/aclEntryForSid/webApplications/${id}?grantedAuthority=${grantedAuth}`)
                .then((acl: ng.IHttpPromiseCallbackArg<AclEntry>) => {
                    return acl.data;
                });
        }

        public canRead(type: string, id: number): ng.IPromise<boolean> {
            return this.backendService.get(this.aclBackendUrl + "/canRead/" + type + "/" + id)
            .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                return result.data;
            });
        }

        public canReadList(type: string, ids: number[]): ng.IPromise<{ [id: number]: boolean; }> {
            return this.backendService.post(this.aclBackendUrl + "/canRead/" + type + "/list", ids)
                .then((result: ng.IHttpPromiseCallbackArg<{ [id: number]: boolean; }>) => {
                    return result.data;
                });
        }

        public canWrite(type: string, id: number): ng.IPromise<boolean> {
            return this.backendService.get(this.aclBackendUrl + "/canWrite/" + type + "/" + id)
                .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return result.data;
                });
        }

        public canWriteList(type: string, ids: number[]): ng.IPromise<boolean[]> {
            return this.backendService.post(this.aclBackendUrl + "/canWrite/" + type + "/list", ids)
                .then((result: ng.IHttpPromiseCallbackArg<boolean[]>) => {
                    return result.data;
                });
        }

        public canCreate(type: string): ng.IPromise<boolean> {
            return this.backendService.get(this.aclBackendUrl + "/canCreate/" + type)
                .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return result.data;
                });
        }

        public canAccessGui(id: number): ng.IPromise<boolean> {
            let request: CanAccessGuiRequest = this.getOrCreateNewCanAccessGuiRequest(id, this.canAccessGuiQueue);
            return request.deferred.promise;
        }

        public canAccessGuiList(requestMap: IdMap): void {
            let ids: number[] = Object.keys(requestMap).map((value: string) => {
                return parseInt(value, 10);
            });

            let request: CanAccessGuiRequest[][] = ids.map((id: number) => {
                return requestMap[id];
            });

            this.$http.post(this.aclBackendUrl + '/canAccessGui/networkNodes/list', ids)
                .then((result: ng.IHttpPromiseCallbackArg<boolean[]>) => {
                    let canAccessGuiList: boolean[] = result.data;
                    canAccessGuiList.forEach((canAcces: boolean, index: number) => {
                        let requestForIndex: CanAccessGuiRequest[] = request[index];
                        for (let r of requestForIndex) {
                            r.deferred.resolve(canAcces);
                        }
                    });
                });
        }

        public getPermissions( type: string, id: number ): ng.IPromise<Models.IEvaluatedAcl> {
            return this.backendService.get(this.aclBackendUrl + '/getPermissions/' + type + "/" + id)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IEvaluatedAcl>) => {
                    return result.data;
                });
        }

        public getPermissionsList( type: string, ids: number[]): ng.IPromise<Models.IEvaluatedAcl[]> {
            return this.backendService.post(this.aclBackendUrl + '/getPermissions/' + type, ids )
                .then((result: ng.IHttpPromiseCallbackArg<Models.IEvaluatedAcl[]>) => {
                    return result.data;
                });
        }

        public saveAcl(acl: AclEntry[]): ng.IPromise<IAclEntries> {
            if (acl.length > 0) {
                let url: string = this.aclUrl(acl);
                return this.backendService.put(url, acl)
                    .then((response: ng.IHttpPromiseCallbackArg<IAclEntries>) => {
                        return response.data;
                    });
            }
        }

        public saveAclEntry(aclEntry: AclEntry): ng.IPromise<AclEntry> {
            return this.backendService.post(this.aclEntryBackendUrl, aclEntry)
                .then((response: ng.IHttpPromiseCallbackArg<AclEntry>) => {
                    return response.data;
                });
        }

        public saveAclEntries(aclEntries: AclEntry[]): ng.IPromise<boolean> {
            return this.backendService.put(this.aclEntriesBackendUrl, aclEntries)
                    .then((response: ng.IHttpPromiseCallbackArg<boolean>) => {
                        return response.data;
                    });
        }

        public changeEntityOrganization(type: string, id: number, organizationId: number): ng.IPromise<number> {
            return this.backendService
                .post(this.aclBackendUrl + "/" + type + "/" + id + "/organization" + organizationId, {})
                .then((result: ng.IHttpPromiseCallbackArg<number>) => {
                    return result.data;
                });
        }

        public getEntityOrganization(type: string, id: number): ng.IPromise<number> {
            return this.backendService
                .get(this.aclBackendUrl + "/" + type + "/" + id + "/organization")
                .then((result: ng.IHttpPromiseCallbackArg<number>) => {
                    return result.data;
                });
        }

        public getEntityOrganizations(tipo: string, id: number): ng.IPromise<number[]> {
            return this.backendService
                .get(this.aclBackendUrl + "/" + tipo + "/" + id + "/organizations")
                .then((result: ng.IHttpPromiseCallbackArg<number[]>) => {
                    return result.data;
                });
        }

        public getEntityIdsFromOrganization(type: string, organizationId: number): ng.IPromise<number[]> {
            return this.backendService
                .get(this.aclBackendUrl + "/organization/" + organizationId + "/type/" + type)
                .then((result: ng.IHttpPromiseCallbackArg<number[]>) => {
                    return result.data;
                });
        }

        private aclUrl(acl: AclEntry[]): string {
            let anAclEntry: AclEntry = acl[0];
            let identityClass: string = anAclEntry.identityClass;
            let id: number = anAclEntry.identityId;
            return this.aclBackendUrl + "/" + identityClass + "/" + id;
        }

        private getOrCreateNewCanAccessGuiRequest(id: number, idMap: IdMap): CanAccessGuiRequest {
            if (idMap[id] === undefined) {
                idMap[id] = [];
            }
            let requests: CanAccessGuiRequest[] = idMap[id];
            let videoFeedRequest: CanAccessGuiRequest = new CanAccessGuiRequest(id, this.$q.defer());
            requests.push(videoFeedRequest);
            return videoFeedRequest;
        }

        private initCanAccessGuiInterval(): void {
            this.doMakeRequests();
            this.$interval(() => {
                this.doMakeRequests();
            }, 1000);
        }

        private doMakeRequests(): void {
            let tempCanAccessGuiQueue: IdMap = this.canAccessGuiQueue;
            this.canAccessGuiQueue = {};
            if (Object.keys(tempCanAccessGuiQueue).length > 0)
                this.canAccessGuiList(tempCanAccessGuiQueue);
        }

    }

    interface IdMap {
        [key: number]: CanAccessGuiRequest[];
    }

    class CanAccessGuiRequest {
        constructor(public id: number,
                    public deferred: ng.IDeferred<boolean>) {
        }

    }
}
